"use client";

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useCartStore } from "@/store/cart.store";
import { cartApi } from "@/features/cart/api/cart.api";
import { Cart } from "@/types/cart.types";
import { CartSchema } from "@/lib/validations/api.schema";
import { UseQueryResult } from "@tanstack/react-query";

export type UseCartResult = UseQueryResult<Cart, AxiosError<unknown>> & {
  addItem: (productId: string, attributeOptionId: string, quantity: number) => Promise<Cart>;
  updateItem: (itemId: string, quantity: number) => Promise<Cart>;
  removeItem: (itemId: string) => Promise<void>;
};

export const cartQueryKeys = {
  all: ["cart"] as const,
};

export const useCart = (
  options?: Partial<UseQueryOptions<Cart, AxiosError<unknown>>>
): UseCartResult => {
  const setCart = useCartStore((s) => s.setCart);
  
  const query = useQuery({
    queryKey: cartQueryKeys.all,
    queryFn: async () => {
      const response = await cartApi.getCart();
      const result = CartSchema.safeParse(response);
      
      if (!result.success) {
        console.warn("Zod Validation Failed for useCart:", result.error);
        const rawCart = response as Cart;
        setCart(rawCart);
        return rawCart;
      }

      setCart(result.data);
      return result.data;
    },
    ...options,
  });

  const addItemMutation = useAddToCart();
  const updateItemMutation = useUpdateCartItem();
  const removeItemMutation = useRemoveCartItem();

  return {
    ...query,
    addItem: (productId: string, attributeOptionId: string, quantity: number) => 
      addItemMutation.mutateAsync({ productId, attributeOptionId, quantity }),
    updateItem: (itemId: string, quantity: number) => 
      updateItemMutation.mutateAsync({ itemId, quantity }),
    removeItem: (itemId: string) => 
      removeItemMutation.mutateAsync(itemId),
  };
};

export const useAddToCart = (
  options?: Partial<
    UseMutationOptions<
      Cart,
      AxiosError,
      { productId: string; attributeOptionId: string; quantity: number },
      unknown
    >
  >
) => {
  const queryClient = useQueryClient();
  const setCart = useCartStore((s) => s.setCart);

  return useMutation({
    mutationFn: ({ productId, attributeOptionId, quantity }) =>
      cartApi.addItem(productId, attributeOptionId, quantity),
    onSuccess: (data, variables, context) => {
      const result = CartSchema.safeParse(data);
      const cartData = result.success ? result.data : (data as Cart);
      
      queryClient.invalidateQueries({ queryKey: cartQueryKeys.all });
      setCart(cartData);
      (options?.onSuccess as any)?.(cartData, variables, context);
    },
    onError: (error, variables, context) => {
      console.error("Failed to add item to cart:", error);
      (options?.onError as any)?.(error, variables, context);
    },
    ...options,
  });
};

export const useUpdateCartItem = (
  options?: Partial<
    UseMutationOptions<Cart, AxiosError, { itemId: string; quantity: number }, unknown>
  >
) => {
  const queryClient = useQueryClient();
  const updateQuantity = useCartStore((s) => s.updateQuantity);
  const setCart = useCartStore((s) => s.setCart);

  return useMutation({
    mutationFn: ({ itemId, quantity }) => cartApi.updateItem(itemId, quantity),
    onSuccess: (data, variables, context) => {
      const result = CartSchema.safeParse(data);
      const cartData = result.success ? result.data : (data as Cart);

      queryClient.invalidateQueries({ queryKey: cartQueryKeys.all });
      // We both update the local store specifically and set the whole cart to stay in sync
      updateQuantity(variables.itemId, variables.quantity);
      setCart(cartData);
      (options?.onSuccess as any)?.(cartData, variables, context);
    },
    onError: (error, variables, context) => {
      console.error("Failed to update cart item:", error);
      (options?.onError as any)?.(error, variables, context);
    },
    ...options,
  });
};

export const useRemoveCartItem = (
  options?: Partial<UseMutationOptions<void, AxiosError, string, unknown>>
) => {
  const queryClient = useQueryClient();
  const removeItem = useCartStore((s) => s.removeItem);

  return useMutation({
    mutationFn: (itemId: string) => cartApi.removeItem(itemId),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: cartQueryKeys.all });
      removeItem(variables);
      (options?.onSuccess as any)?.(data, variables, undefined, context);
    },
    onError: (error, variables, context) => {
      (options?.onError as any)?.(error, variables, undefined, context);
      console.error("Failed to remove item from cart:", error);
    },
    ...options,
  });
};